import React from 'react';
import { useLanguage } from '../i18n';

const NotreApproche = ({ onNavigate }) => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="notre-approche-page">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-green-500/15 border border-green-500/25 text-green-400 text-sm font-medium mb-6">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
              Notre vision de l'IA
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              L'IA accessible,
              <br />
              <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                utile et concrète
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              VF ACTIONS est née de la conviction que l'innovation technologique doit rester 
              accessible à tous les territoires, quelle que soit leur taille. Notre mission : 
              créer un pont entre l'IA et les besoins réels du terrain.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-green-400 text-xs font-mono uppercase tracking-wider">Notre mission</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-6">
                Rendre l'IA utile aux territoires
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Nous accompagnons les collectivités, PME, associations et porteurs de projets 
                dans l'adoption de l'intelligence artificielle. Notre approche est simple : 
                <strong className="text-white"> la technologie doit rester un moyen, jamais une finalité</strong>.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Nous ne cherchons pas à imposer des outils complexes. Nous résolvons des problèmes 
                réels grâce à l'IA, uniquement lorsque cela apporte une véritable valeur ajoutée 
                pour nos clients.
              </p>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-green-500/15 mb-6">
                  <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                </div>
                <blockquote className="text-xl text-white font-medium italic mb-4">
                  "Enfin un acteur de l'IA qui comprend nos réalités et parle notre langage."
                </blockquote>
                <p className="text-gray-500 text-sm">
                  L'objectif que nous poursuivons chaque jour
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Méthode en 4 étapes */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-green-400 text-xs font-mono uppercase tracking-wider">Notre méthode</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
              Une approche en 4 étapes
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Simple, pragmatique et adaptée à vos réalités. Pas de jargon technique, 
              juste des résultats concrets.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Étape 1 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-6 h-full">
                <div className="w-12 h-12 rounded-xl bg-green-500/15 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-green-400">1</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Comprendre</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Nous écoutons vos besoins, analysons votre contexte et identifions 
                  les vrais problèmes à résoudre. Pas de solutions prêtes à l'emploi, 
                  mais une écoute attentive.
                </p>
              </div>
            </div>

            {/* Étape 2 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-6 h-full">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-emerald-400">2</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Identifier</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Nous détectons les opportunités où l'IA peut vraiment faire la différence. 
                  Si une solution simple suffit, nous vous le disons. L'IA n'est pas toujours 
                  la réponse.
                </p>
              </div>
            </div>

            {/* Étape 3 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-6 h-full">
                <div className="w-12 h-12 rounded-xl bg-teal-500/15 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-teal-400">3</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Déployer</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Nous mettons en place des solutions adaptées à votre rythme et à vos équipes. 
                  Formation, prise en main progressive, accompagnement personnalisé.
                </p>
              </div>
            </div>

            {/* Étape 4 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-green-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-6 h-full">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/15 flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-cyan-400">4</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Accompagner</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Nous restons à vos côtés pour accompagner durablement le changement. 
                  Suivi, ajustements, évolutions : notre relation ne s'arrête pas à la livraison.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-20 bg-gray-900/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-green-400 text-xs font-mono uppercase tracking-wider">Nos valeurs</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-4">
              Ce qui guide nos actions
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Des convictions fortes qui se traduisent dans chacune de nos interventions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Proximité */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-500/15 mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Proximité</h3>
              <p className="text-gray-400 text-sm">
                Nous sommes sur le terrain, pas dans une tour d'ivoire technologique. 
                Nous comprenons vos réalités.
              </p>
            </div>

            {/* Transparence */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/15 mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Transparence</h3>
              <p className="text-gray-400 text-sm">
                Pas de promesses irréalistes. Nous vous disons ce qui est possible, 
                ce qui ne l'est pas, et à quel prix.
              </p>
            </div>

            {/* Simplicité */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-500/15 mb-4">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Simplicité</h3>
              <p className="text-gray-400 text-sm">
                Nous parlons votre langage, pas celui des ingénieurs. 
                L'IA doit être compréhensible pour être adoptée.
              </p>
            </div>

            {/* Innovation utile */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500/15 mb-4">
                <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Innovation utile</h3>
              <p className="text-gray-400 text-sm">
                L'IA au service de vos objectifs, pas l'inverse. 
                Nous privilégions l'utilité à la nouveauté.
              </p>
            </div>

            {/* Impact concret */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-500/15 mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Impact concret</h3>
              <p className="text-gray-400 text-sm">
                Des résultats mesurables, pas des effets d'annonce. 
                Nous nous engageons sur des livrables tangibles.
              </p>
            </div>

            {/* Accompagnement humain */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/15 mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Accompagnement humain</h3>
              <p className="text-gray-400 text-sm">
                L'IA ne remplace pas l'humain. Elle lui permet d'agir plus efficacement. 
                Un partenaire à chaque étape.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi les territoires */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-green-400 mb-2">100%</p>
                  <p className="text-gray-400 text-sm">Made in France</p>
                </div>
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-emerald-400 mb-2">RGPD</p>
                  <p className="text-gray-400 text-sm">Données protégées</p>
                </div>
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-teal-400 mb-2">24h</p>
                  <p className="text-gray-400 text-sm">Support réactif</p>
                </div>
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-cyan-400 mb-2">IA</p>
                  <p className="text-gray-400 text-sm">Dernière génération</p>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <span className="text-green-400 text-xs font-mono uppercase tracking-wider">Notre expertise</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-6">
                Pourquoi les territoires ?
              </h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Les collectivités, associations et PME locales sont au cœur de notre économie 
                et de notre vie sociale. Pourtant, elles sont souvent les dernières à bénéficier 
                des avancées technologiques.
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                Nous croyons que <strong className="text-white">l'IA doit servir tous les territoires</strong>, 
                pas seulement les grandes métropoles et les multinationales. C'est pourquoi nous avons 
                développé une offre adaptée aux réalités de terrain.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Notre connaissance des enjeux locaux, notre approche pédagogique et notre engagement 
                sur le long terme font de nous un partenaire de confiance pour votre transformation numérique.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900/30 to-[#0B0F19]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à transformer vos idées en actions ?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Découvrez comment VF ACTIONS peut vous accompagner dans l'adoption de l'IA. 
            Créez votre première stratégie marketing en quelques minutes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button 
              onClick={() => onNavigate('wizard')}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-semibold text-lg transition-all hover:scale-[1.02] shadow-lg shadow-green-500/25"
              data-testid="approche-cta-wizard"
            >
              ✨ Créer ma stratégie
            </button>
            <button 
              onClick={() => onNavigate('home')}
              className="px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium text-lg transition-all"
              data-testid="approche-cta-home"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotreApproche;
